import { go } from '../../App';
import { useLoad, usePolled } from '../../data';
import { InkHero, Loading, MoneyText, Panel, PageHead, formatWhen } from '../../ui';
import { Account, Booking } from '../../types';
import { BookingRecord } from './BookingRecord';

// Confirmed (canon 09 supplier "Confirmed" — the upcoming-jobs schedule). Every
// booking the supplier has said yes to and has not yet worked, soonest first:
// the answer to "what am I doing next". A confirmed booking becomes `completed`
// by the system after its end (the completed sweep), so this list IS the road
// ahead. Gross throughout (DECISION-015) — "You earn", never a guest price.
// Rows drill to the shared Booking-detail Record (the same GET /v1/bookings/{id}
// the hotel uses, field-scoped API-side: the supplier row never carried
// guestAmount or margin).
export function Confirmed({ openId }: { openId?: string }) {
  const { data, error } = usePolled<{ items: Booking[] }>('/bookings?status=confirmed');
  const account = useLoad<Account>('/account');
  const items = data ? data.items : null;
  // Soonest first — a schedule reads forward, not in the byStart order the
  // register wants. The API returns the whole confirmed set; sort here.
  const jobs = (items ?? []).slice().sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  const next = jobs[0];

  return (
    <div>
      <PageHead title="Confirmed" sub="Every job you've said yes to and not yet worked — soonest first." />

      {next && (
        <InkHero
          eyebrow="Next up"
          figure={formatWhen(next.start)}
          explain={`${next.productName} · ${next.hotelName} · ${next.guests.adults + next.guests.children} guest${
            next.guests.adults + next.guests.children === 1 ? '' : 's'
          }`}
        />
      )}

      {error && <p className="error-line">{error}</p>}
      {items === null ? (
        <Loading />
      ) : jobs.length === 0 ? (
        <div className="empty-neutral">No confirmed jobs yet — answered requests land here.</div>
      ) : (
        <Panel style={{ overflow: 'hidden' }} flush>
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>Guest</th>
                <th className="num">You earn</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((b) => (
                <tr key={b.id} className="click" onClick={() => go(`/confirmed/${b.id}`)}>
                  <td className="mono">{formatWhen(b.start)}</td>
                  <td>
                    {b.productName}
                    <br />
                    <span className="hint">{b.hotelName}</span>
                  </td>
                  <td>
                    {b.guestName}
                    {b.guestRoom ? ` · ${b.guestRoom}` : ''}
                  </td>
                  <td className="num">
                    <MoneyText money={b.supplierAmount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
      {openId && <BookingRecord id={openId} backTo="/confirmed" tz={account.data?.timezone ?? 'Europe/Athens'} />}
    </div>
  );
}
